import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { Locale } from '@saniplus/contracts';

import { setUserLocale } from '../auth/authThunks';
import {
  selectIsAuthenticated,
  selectMustChangePin,
  type WithAuthState,
} from '../auth/selectors';
import { configureApiClient } from '../config';
import { setLocale as persistLocale } from '../storage/idb';
import { asLocale } from './createI18n';

export interface UseLocaleOptions {
  /** This app's own IndexedDB name (D-06: device identity is never shared). */
  readonly dbName: string;
}

export interface UseLocaleResult {
  readonly locale: Locale;
  /**
   * The single call a screen makes to change the language. See the package
   * README, "useLocale", for the four steps this replaces.
   */
  readonly setLocale: (next: Locale) => void;
}

type LocaleThunkDispatch = ThunkDispatch<WithAuthState, unknown, UnknownAction>;

/**
 * Amendment #20's client half, consolidated into one hook instead of the
 * four places it is currently spread across in each app (`main.tsx`'s
 * `languageChanged` listener, `LanguageSwitcher.tsx`, `LocaleSync.tsx`, and
 * `configureApiClient`'s `getLocale` closure) — see the README for exactly
 * what each app should migrate.
 *
 * `locale` reads the live i18next language (never IndexedDB directly: i18next
 * is already the source of truth for what is on screen, and re-deriving it
 * from a second store risks the two disagreeing). `setLocale`:
 *
 *  1. flips i18next — every rendered string updates on this call, not the
 *     next render;
 *  2. updates the `?lang=` the base query appends to every request, via
 *     `configureApiClient({ getLocale })`, so the very next request already
 *     carries the new choice;
 *  3. persists to IndexedDB `meta.locale`, so a reload before login still
 *     opens in the language just chosen;
 *  4. once a full session exists, dispatches `setUserLocale` so the choice
 *     also wins server-side — a stored user locale outranks `Accept-Language`
 *     (D-13 / amendment #20's server half).
 */
/**
 * Step 4's guard, on its own so it can be asserted without a renderer.
 *
 * A one-time-PIN token may call `POST /auth/change-pin` and nothing else, so
 * `POST /me/locale` answers 403 and the refusal lands in `state.auth.error` —
 * an error banner on the forced-PIN screen for a toggle that otherwise worked.
 * The three local steps still run; the next screen the user reaches with a
 * full token re-sends the choice.
 */
export function shouldSyncLocaleToServer(authenticated: boolean, mustChangePin: boolean): boolean {
  return authenticated && !mustChangePin;
}

export function useLocale({ dbName }: UseLocaleOptions): UseLocaleResult {
  const { i18n } = useTranslation();
  const dispatch = useDispatch<LocaleThunkDispatch>();
  const authenticated = useSelector<WithAuthState, boolean>(selectIsAuthenticated);
  const mustChangePin = useSelector<WithAuthState, boolean>(selectMustChangePin);
  const locale = useMemo<Locale>(() => asLocale(i18n.language) ?? 'fr', [i18n.language]);

  const setLocale = useCallback(
    (next: Locale) => {
      void i18n.changeLanguage(next);
      configureApiClient({ getLocale: () => next });
      void persistLocale(dbName, next);
      if (shouldSyncLocaleToServer(authenticated, mustChangePin)) dispatch(setUserLocale(next));
    },
    [i18n, dbName, authenticated, mustChangePin, dispatch],
  );

  return { locale, setLocale };
}
